import { useState, useEffect } from "react";
import { Box, Tooltip } from "@mui/material";
import { useParams } from "react-router-dom";
import RootCauseAnalysisDataGrid from "../../components/DataGrids/RootCauseAnalysisDataGrid";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
interface IGenericRootCauseAnalysisPaginatedDataTableProps {
  rootCauseData: any;
  isLoading: boolean;
  isFilterHide: boolean;
  resourceKeys: any;
  pageN: number;
  setPageN: any;
  pageS: number;
  setPageS: any;
  totalPages: number;
  selectedQueryTab: any;
}

// TEMPORARY: keep enabled only while verifying Scenario 1 scroll behavior.
// Remove these constants/helper after scroll testing is complete.
const ENABLE_SCENARIO_1_SCROLL_TEST_DATA = true;
const SCENARIO_1_SCROLL_TEST_TAB = "scenario 1";
const SCENARIO_1_SCROLL_TEST_RESEARCH_QUERY_ID = "438";
const SCENARIO_1_SCROLL_TEST_EXECUTION_ID = "1606";
const SCENARIO_1_SCROLL_TEST_ROW_COUNT = 60;

const buildScenario1ScrollTestRows = (
  rows: any[],
  selectedQueryTab: any,
  researchQueryId?: string,
  executionId?: string
) => {
  const normalizedSelectedQueryTab =
    selectedQueryTab?.toString().trim().toLowerCase() || "";

  if (
    !ENABLE_SCENARIO_1_SCROLL_TEST_DATA ||
    normalizedSelectedQueryTab !== SCENARIO_1_SCROLL_TEST_TAB ||
    researchQueryId !== SCENARIO_1_SCROLL_TEST_RESEARCH_QUERY_ID ||
    executionId !== SCENARIO_1_SCROLL_TEST_EXECUTION_ID ||
    rows.length === 0
  ) {
    return rows;
  }

  return Array.from(
    { length: Math.max(rows.length, SCENARIO_1_SCROLL_TEST_ROW_COUNT) },
    (_, index) => ({
      ...rows[index % rows.length],
      id: index,
    })
  );
};

const GenericRootCauseAnalysisPaginatedDataTable = ({
  rootCauseData,
  isLoading,
  isFilterHide,
  resourceKeys,
  pageN,
  setPageN,
  pageS,
  setPageS,
  totalPages,
  selectedQueryTab,
}: IGenericRootCauseAnalysisPaginatedDataTableProps) => {
  const { researchQueryId, execution_id: executionId } = useParams();
  const { rowsPerPage, rowsPerPageOptions } = useRowsPerPage();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: rowsPerPage,
  });
  const [columnsData, setColumnsData] = useState<any>([]);
  const [rowsData, setRowsData] = useState<any>([]);
  const [errorMessage, setErrorMessage] = useState<JSX.Element | string | null>(
    null
  );

  useEffect(() => {
    const tabData = rootCauseData?.[selectedQueryTab];
    const tabDataArray = Array.isArray(tabData) ? tabData : [];
    if (tabDataArray.some((item: any) => item.hasOwnProperty("error"))) {
      setErrorMessage(tabDataArray[0]?.error);
      setColumnsData([]);
      setRowsData([]);
    } else if (tabDataArray.length > 0) {
      const rows = tabDataArray.map((item: any, index: any) => {
        return {
          id: index,
          ...item,
        };
      });
      const columns = Object.keys(tabDataArray[0] || {}).map((key) => ({
        field: key,
        headerName: (
          <Tooltip title={key} placement="top" arrow>
            <span className="text-capitalize">
              {`${key.slice(0, 15)}${key.length >= 15 ? "..." : ""}`}
            </span>
          </Tooltip>
        ),
        minWidth: 160,
      }));

      // Get all unique values from base_columns in selectedColumns
      const baseColumns = resourceKeys?.base_columns;

      // Filter rows if any value from valuesToMatch is present in any column of the row, excluding 'id' column

      setColumnsData(columns);
      setRowsData(
        buildScenario1ScrollTestRows(
          rows,
          selectedQueryTab,
          researchQueryId,
          executionId
        )
      );
    } else {
      setColumnsData([]);
      setRowsData([]);
    }
  }, [
    executionId,
    researchQueryId,
    rootCauseData,
    resourceKeys,
    selectedQueryTab,
  ]);

  return (
    <RootCauseAnalysisDataGrid
      dataRows={rowsData}
      dataColumns={columnsData}
      loading={isLoading}
      className="dataTable filterMenuBox no-radius pt-0 bdr-top-0"
      disableColumnFilter={true}
      isFilterHide={isFilterHide}
      tableHeight={180}
      rowCount={rowsData.length}
      pageSizeOptions={rowsPerPageOptions}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      paginationMode="client"
      errorMsg={errorMessage}
    />
  );
};

export default GenericRootCauseAnalysisPaginatedDataTable;
